/**
 * One AI task's route in Settings → IA: which provider and model runs it,
 * with how much reasoning effort, in what tone, and under which system
 * prompt. Collapsed by default with the effective route in the header, so
 * "what will review my PR?" is answerable without expanding anything.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  AI_EFFORTS,
  AI_TASKS,
  defaultModelForProvider,
  MAX_AI_SYSTEM_PROMPT_LENGTH,
  resolveAiTaskConfig,
  type AiEffort,
  type AiGenerationSettings,
  type AiTaskId,
  type AiTaskOverride,
  type SettingsAiProvider,
  type SlackReplyTone,
} from '@zunelo/shared';
import { Button } from '../../ui/common/Button';
import { openSettingsSection } from './settingsDeepLink';
import { AI_PROVIDER_LABELS, EFFORT_LABELS, TONE_LABELS } from './aiSettingsLabels';
import { hintStyle, inputStyle, labelStyle } from './settingsStyles';
import type { AiModelCatalogState } from './useAiModelCatalog';

const INHERIT = '__global__';

export interface SettingsAiTaskRowProps {
  task: AiTaskId;
  aiDraft: AiGenerationSettings;
  catalog: AiModelCatalogState;
  configuredProviders: readonly SettingsAiProvider[];
  saving: boolean;
  onOverride: (task: AiTaskId, override: AiTaskOverride) => void;
  onSaveOverride: (task: AiTaskId, override: AiTaskOverride) => void;
}

function providerOptions(
  configured: readonly SettingsAiProvider[],
  inheritLabel: string,
): React.ReactNode {
  // The inherit label carries the global provider's own status: a fresh
  // account inherits `opencode-go` whether or not that key was connected.

  return (
    <>
      <option value={INHERIT}>{inheritLabel}</option>
      {(['opencode-go', 'gemini', 'openai-codex'] as const).map((provider) => (
        <option key={provider} value={provider} disabled={!configured.includes(provider)}>
          {AI_PROVIDER_LABELS[provider]}
          {configured.includes(provider) ? '' : ' · sin conectar'}
        </option>
      ))}
    </>
  );
}

function routeSummary(
  aiDraft: AiGenerationSettings,
  task: AiTaskId,
  override: AiTaskOverride | undefined,
  promptDirty: boolean,
): string {
  const resolved = resolveAiTaskConfig(aiDraft, task);
  const route = resolved.usesGlobalRoute
    ? 'Global'
    : `${AI_PROVIDER_LABELS[resolved.provider]} · ${resolved.model}`;
  // The header used to state only the first of two attempts.
  const fallback = resolved.fallbackProvider === 'none' ? ' · sin reintento' : '';
  const custom = override?.systemPrompt?.trim() ? ' · prompt propio' : '';
  const dirty = promptDirty ? ' · prompt sin guardar' : '';
  return `${route} · ${EFFORT_LABELS[resolved.effort]}${fallback}${custom}${dirty}`;
}

export const SettingsAiTaskRow: React.FC<SettingsAiTaskRowProps> = ({
  task,
  aiDraft,
  catalog,
  configuredProviders,
  saving,
  onOverride,
  onSaveOverride,
}) => {
  const definition = AI_TASKS[task];
  const override = aiDraft.tasks?.[task];
  const savedPrompt = override?.systemPrompt ?? '';
  const [expanded, setExpanded] = useState(false);
  const [promptDraft, setPromptDraft] = useState(savedPrompt);
  const resolved = resolveAiTaskConfig(aiDraft, task);
  const panelId = `settings-ai-task-panel-${task}`;
  const rowRef = useRef<HTMLDivElement>(null);
  const promptFocusedRef = useRef(false);

  const patch = (changes: AiTaskOverride) => {
    const next: AiTaskOverride = { ...override, ...changes };
    for (const key of Object.keys(next) as Array<keyof AiTaskOverride>) {
      if (next[key] === undefined) delete next[key];
    }
    onOverride(task, next);
    onSaveOverride(task, next);
  };

  // Follow the server's answer (it trims and caps the prompt), so the textarea
  // never shows text that was not actually stored — unless the user is typing
  // in it right now, in which case their keystrokes win.
  useEffect(() => {
    if (promptFocusedRef.current) return;
    setPromptDraft(savedPrompt);
  }, [savedPrompt]);

  const savePrompt = (value: string) => {
    if (value.trim() === savedPrompt) return;
    patch({ systemPrompt: value.trim() || undefined });
  };

  /**
   * Saving on blur must not fire when focus is moving to this row's own
   * buttons: the save flips `saving` synchronously, the button disables, and
   * the browser then swallows the click — so "Restaurar valores globales"
   * would persist the very text it was asked to discard.
   */
  const onPromptBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && rowRef.current?.contains(next)) return;
    savePrompt(event.target.value);
  };

  // Everything else in this section auto-saves; a typed prompt used to be the
  // one thing that could be lost by switching section or closing the modal.
  const pendingPromptRef = useRef({ draft: promptDraft, saved: savedPrompt });
  pendingPromptRef.current = { draft: promptDraft, saved: savedPrompt };
  const savePromptRef = useRef(savePrompt);
  savePromptRef.current = savePrompt;
  useEffect(
    () => () => {
      const { draft, saved } = pendingPromptRef.current;
      if (draft.trim() !== saved) savePromptRef.current(draft);
    },
    [],
  );

  return (
    <div
      ref={rowRef}
      data-testid={`settings-ai-task-${task}`}
      style={{ border: '1px solid var(--z-border)', borderRadius: 10, overflow: 'hidden' }}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        data-testid={`settings-ai-task-toggle-${task}`}
        onClick={() => setExpanded((value) => !value)}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 12px',
          border: 0,
          background: 'transparent',
          color: 'var(--z-text)',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 600 }}>{definition.label}</span>
        <span
          data-testid={`settings-ai-task-route-${task}`}
          style={{ color: 'var(--z-dim)', fontSize: 12 }}
        >
          {routeSummary(aiDraft, task, override, promptDraft.trim() !== savedPrompt)}
        </span>
      </button>
      {expanded ? (
        <div
          id={panelId}
          role="region"
          aria-label={definition.label}
          style={{ display: 'grid', gap: 10, padding: '0 12px 12px' }}
        >
          <p style={{ ...hintStyle, margin: 0 }}>{definition.description}</p>
          {catalog.catalog[resolved.provider]?.status === 'error' ? (
            <p
              data-testid={`settings-ai-task-catalog-degraded-${task}`}
              style={{ ...hintStyle, margin: 0 }}
            >
              No se pudo leer la lista de modelos de {AI_PROVIDER_LABELS[resolved.provider]}; se
              muestran los conocidos.
            </p>
          ) : null}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>Proveedor</span>
            <select
              data-testid={`settings-ai-task-provider-${task}`}
              disabled={catalog.loading}
              value={override?.provider ?? INHERIT}
              onChange={(event) => {
                const value = event.target.value;
                if (value === INHERIT) {
                  // Back to the global route: its provider owns its model.
                  patch({ provider: undefined, model: undefined });
                  return;
                }
                const provider = value as SettingsAiProvider;
                // A model from the previous provider's roster cannot run here,
                // so pin one this provider actually lists.
                patch({ provider, model: catalog.defaultModelFor(provider) });
              }}
              style={inputStyle}
            >
              {providerOptions(
                configuredProviders,
                `Usar el global (${AI_PROVIDER_LABELS[aiDraft.provider]}${
                  configuredProviders.includes(aiDraft.provider) ? '' : ' · sin conectar'
                })`,
              )}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>Modelo</span>
            <select
              data-testid={`settings-ai-task-model-${task}`}
              value={override?.model ?? INHERIT}
              onChange={(event) =>
                patch({ model: event.target.value === INHERIT ? undefined : event.target.value })
              }
              style={inputStyle}
            >
              <option value={INHERIT}>
                {override?.provider && override.provider !== aiDraft.provider
                  ? `Por defecto de ${AI_PROVIDER_LABELS[override.provider]} (${defaultModelForProvider(override.provider)})`
                  : `Usar el global (${aiDraft.model})`}
              </option>
              {catalog.optionsFor(resolved.provider, override?.model).map((model) => (
                <option key={model.id} value={model.id}>
                  {model.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>Esfuerzo</span>
            <select
              data-testid={`settings-ai-task-effort-${task}`}
              value={override?.effort ?? INHERIT}
              onChange={(event) =>
                patch({
                  effort:
                    event.target.value === INHERIT ? undefined : (event.target.value as AiEffort),
                })
              }
              style={inputStyle}
            >
              <option value={INHERIT}>
                Por defecto de la tarea ({EFFORT_LABELS[definition.defaultEffort]})
              </option>
              {AI_EFFORTS.map((effort) => (
                <option key={effort} value={effort}>
                  {EFFORT_LABELS[effort]}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>Si falla</span>
            <select
              data-testid={`settings-ai-task-fallback-${task}`}
              value={override?.fallbackProvider ?? INHERIT}
              onChange={(event) => {
                const value = event.target.value;
                if (value === INHERIT) {
                  patch({ fallbackProvider: undefined, fallbackModel: undefined });
                  return;
                }
                if (value === 'none') {
                  patch({ fallbackProvider: 'none', fallbackModel: undefined });
                  return;
                }
                const provider = value as SettingsAiProvider;
                patch({
                  fallbackProvider: provider,
                  fallbackModel: catalog.defaultModelFor(provider),
                });
              }}
              style={inputStyle}
            >
              <option value={INHERIT}>Reintentar con el fallback global</option>
              <option value="none">No reintentar con otro proveedor</option>
              {configuredProviders.map((provider) => (
                <option key={provider} value={provider}>
                  Reintentar con {AI_PROVIDER_LABELS[provider]}
                </option>
              ))}
            </select>
          </label>
          {definition.toneSource === 'task' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={labelStyle}>Tono</span>
              <select
                data-testid={`settings-ai-task-tone-${task}`}
                value={override?.tone ?? INHERIT}
                onChange={(event) =>
                  patch({
                    tone:
                      event.target.value === INHERIT
                        ? undefined
                        : (event.target.value as SlackReplyTone),
                  })
                }
                style={inputStyle}
              >
                <option value={INHERIT}>Sin indicación de tono</option>
                {(Object.keys(TONE_LABELS) as SlackReplyTone[]).map((tone) => (
                  <option key={tone} value={tone}>
                    {TONE_LABELS[tone]}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p style={{ ...hintStyle, margin: 0 }}>
              El tono de los borradores se configura en{' '}
              <button
                type="button"
                data-testid={`settings-ai-task-tone-link-${task}`}
                onClick={() => openSettingsSection('slack')}
                style={{
                  border: 0,
                  background: 'transparent',
                  padding: 0,
                  color: 'var(--z-accent)',
                  cursor: 'pointer',
                }}
              >
                Slack › Voz
              </button>
              .
            </p>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={labelStyle}>System prompt</span>
            <textarea
              data-testid={`settings-ai-task-prompt-${task}`}
              value={promptDraft}
              placeholder={definition.defaultSystemPrompt}
              rows={4}
              maxLength={MAX_AI_SYSTEM_PROMPT_LENGTH}
              onFocus={() => {
                promptFocusedRef.current = true;
              }}
              onChange={(event) => setPromptDraft(event.target.value)}
              onBlur={(event) => {
                promptFocusedRef.current = false;
                onPromptBlur(event);
              }}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
            <span
              data-testid={`settings-ai-task-prompt-count-${task}`}
              style={{ ...hintStyle, alignSelf: 'flex-end' }}
            >
              {promptDraft.length}/{MAX_AI_SYSTEM_PROMPT_LENGTH}
            </span>
          </label>
          <p style={{ ...hintStyle, margin: 0 }}>
            Se guarda solo al salir del campo o al cerrar Settings; usa «Guardar prompt» si quieres
            confirmarlo ahora. El contrato JSON y la regla de no seguir instrucciones del contenido
            se aplican siempre, aunque cambies este prompt.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              size="sm"
              variant="primary"
              data-testid={`settings-ai-task-prompt-save-${task}`}
              disabled={saving || promptDraft.trim() === savedPrompt}
              onClick={() => savePrompt(promptDraft)}
            >
              Guardar prompt
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid={`settings-ai-task-reset-${task}`}
              disabled={saving || !override}
              onClick={() => {
                setPromptDraft('');
                onOverride(task, {});
                onSaveOverride(task, {});
              }}
            >
              Restaurar valores globales
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
